import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  X, Hash, Users, Shield, Trash2, Edit2, Plus, Save, ChevronRight,
  ScrollText, Gavel, AlertTriangle, Ban, Clock, UserX, Eye, Settings, Bot, Flag
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ServerSettings = ({ onClose }) => {
  const { server, categories, channels, roles, members, fetchChannels, fetchCategories, fetchRoles, fetchMembers, fetchServer } = useApp();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [section, setSection] = useState('overview');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [userPerms, setUserPerms] = useState(0);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'
  }), [token]);

  useEffect(() => {
    fetch(`${API_URL}/api/server/admin-check`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setIsAdmin(d.is_admin);
        setIsOwner(d.is_owner);
        setUserPerms(d.permissions || 0);
      })
      .catch(() => {});
  }, [token]);

  const hasPerm = (flag) => Boolean(userPerms & flag);

  const allSections = [
    { id: 'overview', label: 'Overview', icon: Settings, show: true },
    { id: 'channels', label: t('app.channels'), icon: Hash, show: true },
    { id: 'roles', label: t('app.roles'), icon: Shield, show: true },
    { id: 'members', label: t('app.members'), icon: Users, show: true },
    { id: 'moderation', label: 'Moderation', icon: Gavel, show: hasPerm(16) },
    { id: 'automod', label: 'AutoMod', icon: Bot, show: hasPerm(32) },
    { id: 'reports', label: 'Reports', icon: Flag, show: hasPerm(16) },
    { id: 'audit', label: 'Audit Log', icon: ScrollText, show: hasPerm(64) },
  ];

  const sections = allSections.filter(s => s.show);

  return (
    <div className="fixed inset-0 z-50 bg-[#0b0d10] flex" data-testid="server-settings">
      <div className="w-56 bg-[#0e1015] border-r border-white/5 flex flex-col">
        <div className="h-12 px-4 flex items-center border-b border-white/5">
          <span className="text-sm font-semibold text-white">{t('app.serverSettings')}</span>
        </div>
        <ScrollArea className="flex-1 px-2 py-2">
          {sections.map(s => (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`ss-${s.id}`}
              className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors ${
                section === s.id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}>
              <s.icon className="h-4 w-4" />{s.label}
            </button>
          ))}
        </ScrollArea>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="h-12 px-6 flex items-center justify-between border-b border-white/5">
          <h2 className="text-sm font-semibold text-white">{sections.find(s => s.id === section)?.label}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1 rounded hover:bg-white/10" data-testid="close-server-settings">
            <X className="h-5 w-5" />
          </button>
        </div>
        <ScrollArea className="flex-1 p-6">
          {section === 'overview' && <OverviewSection server={server} members={members} channels={channels} roles={roles} isOwner={isOwner} authHeaders={authHeaders} fetchServer={fetchServer} />}
          {section === 'channels' && <ChannelsSection canManage={hasPerm(4)} categories={categories} channels={channels} authHeaders={authHeaders} fetchChannels={fetchChannels} fetchCategories={fetchCategories} />}
          {section === 'roles' && <RolesSection canManage={hasPerm(8)} isOwner={isOwner} roles={roles} members={members} authHeaders={authHeaders} fetchRoles={fetchRoles} fetchMembers={fetchMembers} token={token} />}
          {section === 'members' && <MembersSection canManageRoles={hasPerm(8)} canManageMembers={hasPerm(16)} members={members} roles={roles} authHeaders={authHeaders} fetchMembers={fetchMembers} />}
          {section === 'moderation' && <ModerationSection authHeaders={authHeaders} fetchMembers={fetchMembers} />}
          {section === 'automod' && <AutomodSection token={token} authHeaders={authHeaders} />}
          {section === 'reports' && <ReportsSection token={token} authHeaders={authHeaders} />}
          {section === 'audit' && <AuditLogSection token={token} />}
        </ScrollArea>
      </div>
    </div>
  );
};

// ==================== OVERVIEW ====================
const OverviewSection = ({ server, members, channels, roles, isOwner, authHeaders, fetchServer }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(server?.name || '');
  const [desc, setDesc] = useState(server?.description || '');

  const handleSave = async () => {
    const res = await fetch(`${API_URL}/api/server`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ name, description: desc })
    });
    if (res.ok) {
      toast.success('Server updated');
      setEditing(false);
      fetchServer();
    } else {
      const e = await res.json();
      toast.error(e.detail);
    }
  };

  return (
    <div className="max-w-lg space-y-4" data-testid="ss-overview-content">
      <div className="bg-[#12141a] rounded-lg p-4 border border-white/5">
        {editing ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400 uppercase">Server Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="edit-server-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 uppercase">Description</Label>
              <Input value={desc} onChange={e => setDesc(e.target.value)} className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="edit-server-desc" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-xs" data-testid="save-server-btn"><Save className="h-3.5 w-3.5 mr-1" />Save</Button>
              <Button onClick={() => setEditing(false)} size="sm" variant="outline" className="border-white/10 text-xs">Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-white mb-1">{server?.name}</h3>
                <p className="text-sm text-zinc-400">{server?.description}</p>
              </div>
              {isOwner && (
                <button onClick={() => setEditing(true)} className="text-zinc-500 hover:text-white p-1" data-testid="edit-server-overview-btn">
                  <Edit2 className="h-4 w-4" />
                </button>
              )}
            </div>
            {isOwner && <span className="inline-block mt-2 text-[10px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-full font-medium">Owner</span>}
          </>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Members" value={members.length} />
        <StatCard label="Channels" value={channels.length} />
        <StatCard label="Roles" value={roles.length} />
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-[#12141a] rounded-lg p-3 border border-white/5 text-center">
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-zinc-500">{label}</p>
  </div>
);

// ==================== CHANNELS ====================
const ChannelsSection = ({ canManage, categories, channels, authHeaders, fetchChannels, fetchCategories }) => {
  const [editChannel, setEditChannel] = useState(null);
  const [editName, setEditName] = useState('');

  const handleDeleteChannel = async (chId) => {
    if (!window.confirm('Delete this channel?')) return;
    await fetch(`${API_URL}/api/server/channels/${chId}`, { method: 'DELETE', headers: authHeaders() });
    fetchChannels();
    toast.success('Channel deleted');
  };

  const handleEditChannel = async () => {
    if (!editName.trim()) return;
    await fetch(`${API_URL}/api/server/channels/${editChannel}`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ name: editName })
    });
    setEditChannel(null);
    fetchChannels();
    toast.success('Channel updated');
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    await fetch(`${API_URL}/api/server/categories/${catId}`, { method: 'DELETE', headers: authHeaders() });
    fetchCategories();
    fetchChannels();
    toast.success('Category deleted');
  };

  const channelsByCategory = {};
  const uncategorized = [];
  channels.forEach(ch => {
    if (ch.category_id) {
      if (!channelsByCategory[ch.category_id]) channelsByCategory[ch.category_id] = [];
      channelsByCategory[ch.category_id].push(ch);
    } else {
      uncategorized.push(ch);
    }
  });

  return (
    <div className="max-w-lg space-y-4" data-testid="ss-channels-content">
      {!canManage && <p className="text-sm text-zinc-500 bg-zinc-800/50 px-3 py-2 rounded-lg">View only — permission required to manage channels.</p>}
      {uncategorized.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-zinc-500 uppercase">Uncategorized</p>
          {uncategorized.map(ch => (
            <ChannelRow key={ch.channel_id} channel={ch} canManage={canManage}
              onEdit={() => { setEditChannel(ch.channel_id); setEditName(ch.name); }}
              onDelete={() => handleDeleteChannel(ch.channel_id)} />
          ))}
        </div>
      )}
      {categories.map(cat => (
        <div key={cat.category_id} className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-zinc-500 uppercase">{cat.name}</p>
            {canManage && (
              <button onClick={() => handleDeleteCategory(cat.category_id)} className="text-zinc-600 hover:text-red-400 p-1" data-testid={`delete-cat-${cat.category_id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
          {(channelsByCategory[cat.category_id] || []).map(ch => (
            <ChannelRow key={ch.channel_id} channel={ch} canManage={canManage}
              onEdit={() => { setEditChannel(ch.channel_id); setEditName(ch.name); }}
              onDelete={() => handleDeleteChannel(ch.channel_id)} />
          ))}
        </div>
      ))}
      <Dialog open={!!editChannel} onOpenChange={() => setEditChannel(null)}>
        <DialogContent className="bg-[#12141a] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Edit Channel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400 uppercase">Name</Label>
              <Input value={editName} onChange={e => setEditName(e.target.value)} className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="edit-channel-name" />
            </div>
            <Button onClick={handleEditChannel} className="w-full bg-cyan-600 hover:bg-cyan-700 text-sm" data-testid="save-channel-edit">
              <Save className="h-4 w-4 mr-1" />Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ChannelRow = ({ channel, canManage, onEdit, onDelete }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-[#12141a] rounded-lg border border-white/5 group" data-testid={`ss-ch-${channel.channel_id}`}>
    <Hash className="h-4 w-4 text-zinc-500 flex-shrink-0" />
    <span className="text-sm text-zinc-200 flex-1 truncate">{channel.name}</span>
    <span className="text-[10px] text-zinc-600 px-1.5 py-0.5 bg-white/5 rounded">{channel.channel_type}</span>
    {canManage && (
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onEdit} className="text-zinc-500 hover:text-white p-1"><Edit2 className="h-3.5 w-3.5" /></button>
        <button onClick={onDelete} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    )}
  </div>
);

// ==================== ROLES ====================
const PERM_FLAGS = [
  { key: 'SEND_MESSAGES', value: 1, label: 'Send Messages', desc: 'Send messages in text channels' },
  { key: 'MANAGE_MESSAGES', value: 2, label: 'Manage Messages', desc: 'Delete/pin others\' messages' },
  { key: 'MANAGE_CHANNELS', value: 4, label: 'Manage Channels', desc: 'Create, edit, delete channels' },
  { key: 'MANAGE_ROLES', value: 8, label: 'Manage Roles', desc: 'Create, edit, assign roles' },
  { key: 'MANAGE_MEMBERS', value: 16, label: 'Manage Members', desc: 'Kick, ban, timeout members' },
  { key: 'MANAGE_SERVER', value: 32, label: 'Manage Server', desc: 'Edit server name and settings' },
  { key: 'VIEW_AUDIT_LOG', value: 64, label: 'View Audit Log', desc: 'Access the audit log' },
  { key: 'MENTION_EVERYONE', value: 128, label: 'Mention Everyone', desc: 'Use @everyone and @here' },
  { key: 'USE_MEDIA', value: 512, label: 'Use Emojis/GIFs/Stickers', desc: 'Send emoji, GIFs or stickers in chat' },
  { key: 'ADMINISTRATOR', value: 256, label: 'Administrator', desc: 'Full access to all permissions' },
];

const RolesSection = ({ canManage, isOwner, roles, members, authHeaders, fetchRoles, fetchMembers, token }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#3498DB');
  const [editingRole, setEditingRole] = useState(null);
  const [editPerms, setEditPerms] = useState(0);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  const handleCreateRole = async () => {
    if (!newName.trim()) return;
    await fetch(`${API_URL}/api/server/roles`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ name: newName, color: newColor, permissions: 1 })
    });
    setShowCreate(false);
    setNewName('');
    fetchRoles();
    toast.success('Role created');
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Delete this role?')) return;
    const res = await fetch(`${API_URL}/api/server/roles/${roleId}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { fetchRoles(); toast.success('Role deleted'); }
    else { const e = await res.json(); toast.error(e.detail); }
  };

  const openPermEditor = (role) => {
    setEditingRole(role);
    setEditPerms(role.permissions || 0);
    setEditName(role.name);
    setEditColor(role.color);
  };

  const togglePerm = (flag) => {
    setEditPerms(prev => prev ^ flag);
  };

  const saveRolePerms = async () => {
    await fetch(`${API_URL}/api/server/roles/${editingRole.role_id}`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ name: editName, color: editColor, permissions: editPerms })
    });
    setEditingRole(null);
    fetchRoles();
    toast.success('Role updated');
  };

  return (
    <div className="max-w-lg space-y-3" data-testid="ss-roles-content">
      {!canManage && <p className="text-sm text-zinc-500 bg-zinc-800/50 px-3 py-2 rounded-lg">View only — permission required to manage roles.</p>}

      {roles.map(r => {
        const count = members.filter(m => m.roles?.includes(r.role_id)).length;
        const isBuiltIn = ['role_owner', 'role_admin', 'role_mod', 'role_member'].includes(r.role_id);
        return (
          <div key={r.role_id} className="flex items-center gap-3 px-3 py-2.5 bg-[#12141a] rounded-lg border border-white/5 group" data-testid={`ss-role-${r.role_id}`}>
            <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
            <span className="text-sm text-zinc-200 flex-1">{r.name}</span>
            <span className="text-xs text-zinc-600">{count} members</span>
            {canManage && (
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openPermEditor(r)} className="text-zinc-500 hover:text-cyan-400 p-1" data-testid={`edit-role-${r.role_id}`}>
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
                {!isBuiltIn && (
                  <button onClick={() => handleDeleteRole(r.role_id)} className="text-zinc-600 hover:text-red-400 p-1" data-testid={`delete-role-${r.role_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {canManage && (
        <>
          <Button onClick={() => setShowCreate(true)} variant="outline" className="w-full border-white/10 text-sm h-9 hover:bg-white/5" data-testid="create-role-btn">
            <Plus className="h-4 w-4 mr-1" />Create Role
          </Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogContent className="bg-[#12141a] border-white/10 text-white max-w-sm">
              <DialogHeader><DialogTitle>Create Role</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-zinc-400 uppercase">Name</Label>
                  <Input value={newName} onChange={e => setNewName(e.target.value)} className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="new-role-name" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400 uppercase">Color</Label>
                  <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} className="mt-1 h-9 w-full rounded bg-[#0b0d10] border border-white/5 cursor-pointer" data-testid="new-role-color" />
                </div>
                <Button onClick={handleCreateRole} className="w-full bg-cyan-600 hover:bg-cyan-700 text-sm" data-testid="create-role-submit">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Permission Editor Dialog */}
      <Dialog open={!!editingRole} onOpenChange={() => setEditingRole(null)}>
        <DialogContent className="bg-[#12141a] border-white/10 text-white max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Edit Role — {editingRole?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mb-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <Label className="text-xs text-zinc-400 uppercase">Name</Label>
                <Input value={editName} onChange={e => setEditName(e.target.value)} className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="edit-role-name" />
              </div>
              <div className="w-20">
                <Label className="text-xs text-zinc-400 uppercase">Color</Label>
                <input type="color" value={editColor} onChange={e => setEditColor(e.target.value)} className="mt-1 h-9 w-full rounded bg-[#0b0d10] border border-white/5 cursor-pointer" />
              </div>
            </div>
          </div>
          <ScrollArea className="flex-1 -mx-6 px-6">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Permissions</p>
            <div className="space-y-1">
              {PERM_FLAGS.map(p => {
                const isOn = Boolean(editPerms & p.value);
                const isAdminFlag = p.key === 'ADMINISTRATOR';
                const disabled = isAdminFlag && !isOwner;
                return (
                  <div key={p.key} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${
                    isOn ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-[#0e1015] border-white/5'
                  } ${disabled ? 'opacity-50' : ''}`} data-testid={`perm-${p.key}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-200 font-medium">{p.label}</p>
                      <p className="text-[11px] text-zinc-500">{p.desc}</p>
                    </div>
                    <Switch checked={isOn} onCheckedChange={() => !disabled && togglePerm(p.value)} disabled={disabled} data-testid={`perm-switch-${p.key}`} />
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <div className="pt-3 border-t border-white/5 mt-3">
            <Button onClick={saveRolePerms} className="w-full bg-cyan-600 hover:bg-cyan-700 text-sm" data-testid="save-role-perms">
              <Save className="h-4 w-4 mr-1" />Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MEMBERS ====================
const MembersSection = ({ canManageRoles, canManageMembers, members, roles, authHeaders, fetchMembers }) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberRoles, setMemberRoles] = useState([]);
  const [modAction, setModAction] = useState(null);
  const [modReason, setModReason] = useState('');
  const [timeoutDuration, setTimeoutDuration] = useState(10);

  const openRoleEditor = (member) => {
    setSelectedMember(member);
    setMemberRoles(member.roles || []);
  };

  const toggleRole = (roleId) => {
    setMemberRoles(prev => prev.includes(roleId) ? prev.filter(r => r !== roleId) : [...prev, roleId]);
  };

  const saveRoles = async () => {
    await fetch(`${API_URL}/api/server/members/${selectedMember.user_id}/roles`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify(memberRoles)
    });
    setSelectedMember(null);
    fetchMembers();
    toast.success('Roles updated');
  };

  const handleModAction = async () => {
    if (!modAction) return;
    const body = { reason: modReason || undefined };
    if (modAction.type === 'timeout') body.duration_minutes = timeoutDuration;

    const url = `${API_URL}/api/server/members/${modAction.userId}/${modAction.type}`;
    const res = await fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
    if (res.ok) {
      toast.success(`${modAction.type} applied`);
      fetchMembers();
    } else {
      const e = await res.json();
      toast.error(e.detail);
    }
    setModAction(null);
    setModReason('');
  };

  return (
    <div className="max-w-lg space-y-2" data-testid="ss-members-content">
      <p className="text-xs text-zinc-500 mb-2">{members.length} members</p>
      {members.map(m => (
        <div key={m.user_id} className="flex items-center gap-3 px-3 py-2 bg-[#12141a] rounded-lg border border-white/5 group" data-testid={`ss-member-${m.user_id}`}>
          <Avatar className="h-8 w-8">
            <AvatarImage src={m.avatar} />
            <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">{(m.display_name || m.username || '?')[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{m.display_name || m.username}</p>
            <div className="flex gap-1 flex-wrap">
              {(m.roles || []).map(rId => {
                const role = roles.find(r => r.role_id === rId);
                return role ? (
                  <span key={rId} className="text-[10px] px-1 py-0 rounded" style={{ color: role.color, backgroundColor: role.color + '20' }}>{role.name}</span>
                ) : null;
              })}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {canManageRoles && (
              <button onClick={() => openRoleEditor(m)} className="text-zinc-600 hover:text-white p-1" data-testid={`edit-member-roles-${m.user_id}`}>
                <Shield className="h-4 w-4" />
              </button>
            )}
            {canManageMembers && (
              <>
                <button onClick={() => setModAction({ userId: m.user_id, type: 'warn', name: m.display_name || m.username })} className="text-zinc-600 hover:text-yellow-400 p-1" title="Warn" data-testid={`warn-btn-${m.user_id}`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setModAction({ userId: m.user_id, type: 'timeout', name: m.display_name || m.username })} className="text-zinc-600 hover:text-orange-400 p-1" title="Timeout" data-testid={`timeout-btn-${m.user_id}`}>
                  <Clock className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setModAction({ userId: m.user_id, type: 'kick', name: m.display_name || m.username })} className="text-zinc-600 hover:text-red-400 p-1" title="Kick" data-testid={`kick-btn-${m.user_id}`}>
                  <UserX className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setModAction({ userId: m.user_id, type: 'ban', name: m.display_name || m.username })} className="text-zinc-600 hover:text-red-500 p-1" title="Ban" data-testid={`ban-btn-${m.user_id}`}>
                  <Ban className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
      ))}

      {/* Role editor dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="bg-[#12141a] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Edit Roles — {selectedMember?.display_name || selectedMember?.username}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {roles.map(r => (
              <button key={r.role_id} onClick={() => toggleRole(r.role_id)} data-testid={`toggle-role-${r.role_id}`}
                className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm border transition-colors ${
                  memberRoles.includes(r.role_id) ? 'border-cyan-500/30 bg-cyan-500/10' : 'border-white/5 hover:border-white/10'
                }`}>
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: r.color }} />
                <span className="text-zinc-200">{r.name}</span>
                {memberRoles.includes(r.role_id) && <span className="ml-auto text-cyan-400 text-xs">Active</span>}
              </button>
            ))}
            <Button onClick={saveRoles} className="w-full bg-cyan-600 hover:bg-cyan-700 text-sm mt-2" data-testid="save-member-roles">
              <Save className="h-4 w-4 mr-1" />Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Moderation action dialog */}
      <Dialog open={!!modAction} onOpenChange={() => setModAction(null)}>
        <DialogContent className="bg-[#12141a] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {modAction?.type === 'warn' && <AlertTriangle className="h-5 w-5 text-yellow-400" />}
              {modAction?.type === 'timeout' && <Clock className="h-5 w-5 text-orange-400" />}
              {modAction?.type === 'kick' && <UserX className="h-5 w-5 text-red-400" />}
              {modAction?.type === 'ban' && <Ban className="h-5 w-5 text-red-500" />}
              {modAction?.type?.charAt(0).toUpperCase() + modAction?.type?.slice(1)} — {modAction?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400 uppercase">Reason</Label>
              <Input value={modReason} onChange={e => setModReason(e.target.value)} placeholder="Reason (optional)" className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="mod-reason-input" />
            </div>
            {modAction?.type === 'timeout' && (
              <div>
                <Label className="text-xs text-zinc-400 uppercase">Duration (minutes)</Label>
                <Input type="number" value={timeoutDuration} onChange={e => setTimeoutDuration(parseInt(e.target.value) || 10)} className="mt-1 bg-[#0b0d10] border-white/5 text-sm" data-testid="timeout-duration" />
              </div>
            )}
            <Button onClick={handleModAction} data-testid="confirm-mod-action"
              className={`w-full text-sm ${modAction?.type === 'ban' ? 'bg-red-600 hover:bg-red-700' : modAction?.type === 'kick' ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-600 hover:bg-orange-700'}`}>
              Confirm {modAction?.type}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MODERATION ====================
const ModerationSection = ({ authHeaders, fetchMembers }) => {
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/server/bans`, { headers: authHeaders() });
      if (res.ok) setBans(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { fetchBans(); }, [fetchBans]);

  const handleUnban = async (userId) => {
    const res = await fetch(`${API_URL}/api/server/bans/${userId}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) {
      toast.success('User unbanned');
      fetchBans();
    }
  };

  return (
    <div className="max-w-lg space-y-4" data-testid="ss-moderation-content">
      <div className="bg-[#12141a] rounded-lg p-4 border border-white/5">
        <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
          <Ban className="h-4 w-4 text-red-400" /> Banned Users
        </h3>
        <p className="text-xs text-zinc-500 mb-3">Users banned from the server</p>
        {loading ? (
          <p className="text-xs text-zinc-600">Loading...</p>
        ) : bans.length === 0 ? (
          <p className="text-xs text-zinc-600 text-center py-4">No banned users</p>
        ) : (
          <div className="space-y-2">
            {bans.map(b => (
              <div key={b.user_id} className="flex items-center gap-3 px-3 py-2 bg-[#0b0d10] rounded-lg border border-white/5" data-testid={`ban-${b.user_id}`}>
                <Avatar className="h-7 w-7">
                  <AvatarImage src={b.user?.avatar} />
                  <AvatarFallback className="bg-red-600/20 text-red-300 text-[10px]">{(b.user?.username || '?')[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{b.user?.display_name || b.user?.username || 'Unknown'}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{b.reason}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleUnban(b.user_id)} className="border-white/10 text-xs h-7 hover:bg-white/5 hover:text-green-400" data-testid={`unban-${b.user_id}`}>
                  Unban
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== AUDIT LOG ====================
const AUDIT_ICONS = {
  CHANNEL_CREATE: { icon: Plus, color: 'text-green-400' },
  CHANNEL_DELETE: { icon: Trash2, color: 'text-red-400' },
  CHANNEL_UPDATE: { icon: Edit2, color: 'text-blue-400' },
  CATEGORY_CREATE: { icon: Plus, color: 'text-green-400' },
  CATEGORY_DELETE: { icon: Trash2, color: 'text-red-400' },
  ROLE_CREATE: { icon: Plus, color: 'text-green-400' },
  ROLE_UPDATE: { icon: Edit2, color: 'text-blue-400' },
  ROLE_DELETE: { icon: Trash2, color: 'text-red-400' },
  MEMBER_ROLES_UPDATE: { icon: Shield, color: 'text-cyan-400' },
  MEMBER_KICK: { icon: UserX, color: 'text-orange-400' },
  MEMBER_BAN: { icon: Ban, color: 'text-red-500' },
  MEMBER_UNBAN: { icon: Users, color: 'text-green-400' },
  MEMBER_TIMEOUT: { icon: Clock, color: 'text-orange-400' },
  MEMBER_WARN: { icon: AlertTriangle, color: 'text-yellow-400' },
  SERVER_UPDATE: { icon: Settings, color: 'text-cyan-400' },
};

const AuditLogSection = ({ token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAudit = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/server/audit-log`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setEntries(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAudit();
  }, [token]);

  return (
    <div className="max-w-2xl space-y-2" data-testid="ss-audit-content">
      {loading ? (
        <p className="text-xs text-zinc-600">Loading audit log...</p>
      ) : entries.length === 0 ? (
        <div className="text-center py-12">
          <ScrollText className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No audit log entries yet</p>
          <p className="text-xs text-zinc-600 mt-1">Actions performed by moderators will appear here</p>
        </div>
      ) : (
        entries.map(entry => {
          const cfg = AUDIT_ICONS[entry.action] || { icon: Eye, color: 'text-zinc-400' };
          const IconComp = cfg.icon;
          const time = new Date(entry.created_at);
          const details = entry.details || {};

          return (
            <div key={entry.audit_id} className="flex gap-3 px-3 py-2.5 bg-[#12141a] rounded-lg border border-white/5" data-testid={`audit-${entry.audit_id}`}>
              <div className={`mt-0.5 ${cfg.color}`}>
                <IconComp className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-white">{entry.user?.display_name || entry.user?.username || 'System'}</span>
                  <span className="text-[11px] text-zinc-500">{entry.action.replace(/_/g, ' ').toLowerCase()}</span>
                </div>
                {details.name && <p className="text-xs text-zinc-400 mt-0.5">Name: {details.name}</p>}
                {details.reason && <p className="text-xs text-zinc-400 mt-0.5">Reason: {details.reason}</p>}
                {details.target_username && <p className="text-xs text-zinc-400 mt-0.5">Target: {details.target_username}</p>}
                {details.duration_minutes && <p className="text-xs text-zinc-400 mt-0.5">Duration: {details.duration_minutes} min</p>}
                {details.new_roles && <p className="text-xs text-zinc-400 mt-0.5">Roles: {details.new_roles.join(', ')}</p>}
              </div>
              <div className="text-[10px] text-zinc-600 flex-shrink-0">
                {time.toLocaleDateString()} {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// ==================== AUTOMOD ====================
const AutomodSection = ({ token, authHeaders }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newWord, setNewWord] = useState('');

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/server/automod`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setConfig(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const updateConfig = async (updates) => {
    const res = await fetch(`${API_URL}/api/server/automod`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify(updates)
    });
    if (res.ok) {
      setConfig(await res.json());
      toast.success('AutoMod updated');
    }
  };

  const addWord = () => {
    if (!newWord.trim() || !config) return;
    const words = [...(config.blocked_words || []), newWord.trim()];
    updateConfig({ blocked_words: words });
    setNewWord('');
  };

  const removeWord = (word) => {
    if (!config) return;
    const words = (config.blocked_words || []).filter(w => w !== word);
    updateConfig({ blocked_words: words });
  };

  if (loading || !config) return <p className="text-xs text-zinc-600">Loading...</p>;

  return (
    <div className="max-w-lg space-y-4" data-testid="ss-automod-content">
      {/* Spam Protection */}
      <div className="bg-[#12141a] rounded-lg p-4 border border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Spam Protection</h3>
            <p className="text-[11px] text-zinc-500">Rate limit messages to prevent spam</p>
          </div>
          <Switch checked={config.spam_enabled} onCheckedChange={v => updateConfig({ spam_enabled: v })} data-testid="spam-toggle" />
        </div>
        {config.spam_enabled && (
          <div className="flex gap-3">
            <div className="flex-1">
              <Label className="text-[10px] text-zinc-500 uppercase">Max Messages</Label>
              <Input type="number" value={config.spam_max_messages} onChange={e => updateConfig({ spam_max_messages: parseInt(e.target.value) || 5 })}
                className="mt-1 bg-[#0b0d10] border-white/5 text-sm h-8" data-testid="spam-max-msgs" />
            </div>
            <div className="flex-1">
              <Label className="text-[10px] text-zinc-500 uppercase">Window (sec)</Label>
              <Input type="number" value={config.spam_window_seconds} onChange={e => updateConfig({ spam_window_seconds: parseInt(e.target.value) || 5 })}
                className="mt-1 bg-[#0b0d10] border-white/5 text-sm h-8" data-testid="spam-window" />
            </div>
          </div>
        )}
      </div>

      {/* Word Filter */}
      <div className="bg-[#12141a] rounded-lg p-4 border border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Word Filter</h3>
            <p className="text-[11px] text-zinc-500">Block messages containing specific words</p>
          </div>
          <Switch checked={config.word_filter_enabled} onCheckedChange={v => updateConfig({ word_filter_enabled: v })} data-testid="wordfilter-toggle" />
        </div>
        {config.word_filter_enabled && (
          <>
            <div className="flex gap-2 mb-2">
              <Input value={newWord} onChange={e => setNewWord(e.target.value)} placeholder="Add blocked word..."
                className="bg-[#0b0d10] border-white/5 text-sm h-8 flex-1" data-testid="add-word-input"
                onKeyDown={e => e.key === 'Enter' && addWord()} />
              <Button onClick={addWord} size="sm" className="bg-cyan-600 hover:bg-cyan-700 h-8 text-xs" data-testid="add-word-btn">Add</Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {(config.blocked_words || []).map(w => (
                <span key={w} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/20 rounded-full text-xs text-red-400">
                  {w}
                  <button onClick={() => removeWord(w)} className="hover:text-white"><X className="h-3 w-3" /></button>
                </span>
              ))}
              {(config.blocked_words || []).length === 0 && <p className="text-xs text-zinc-600">No blocked words</p>}
            </div>
          </>
        )}
      </div>

      {/* Anti-Raid */}
      <div className="bg-[#12141a] rounded-lg p-4 border border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Anti-Raid</h3>
            <p className="text-[11px] text-zinc-500">Block mass registrations</p>
          </div>
          <Switch checked={config.anti_raid_enabled} onCheckedChange={v => updateConfig({ anti_raid_enabled: v })} data-testid="antiraid-toggle" />
        </div>
        {config.anti_raid_enabled && (
          <div className="flex gap-3">
            <div className="flex-1">
              <Label className="text-[10px] text-zinc-500 uppercase">Max Joins</Label>
              <Input type="number" value={config.anti_raid_join_threshold} onChange={e => updateConfig({ anti_raid_join_threshold: parseInt(e.target.value) || 10 })}
                className="mt-1 bg-[#0b0d10] border-white/5 text-sm h-8" data-testid="antiraid-threshold" />
            </div>
            <div className="flex-1">
              <Label className="text-[10px] text-zinc-500 uppercase">Window (sec)</Label>
              <Input type="number" value={config.anti_raid_window_seconds} onChange={e => updateConfig({ anti_raid_window_seconds: parseInt(e.target.value) || 10 })}
                className="mt-1 bg-[#0b0d10] border-white/5 text-sm h-8" data-testid="antiraid-window" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== REPORTS ====================
const ReportsSection = ({ token, authHeaders }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setReports(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const resolveReport = async (reportId) => {
    const res = await fetch(`${API_URL}/api/reports/${reportId}`, { method: 'PUT', headers: authHeaders() });
    if (res.ok) {
      toast.success('Report resolved');
      fetchReports();
    }
  };

  return (
    <div className="max-w-lg space-y-2" data-testid="ss-reports-content">
      {loading ? (
        <p className="text-xs text-zinc-600">Loading reports...</p>
      ) : reports.length === 0 ? (
        <div className="text-center py-12">
          <Flag className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No reports</p>
          <p className="text-xs text-zinc-600 mt-1">User reports will appear here</p>
        </div>
      ) : (
        reports.map(r => (
          <div key={r.report_id} className="bg-[#12141a] rounded-lg p-3 border border-white/5" data-testid={`report-${r.report_id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  r.status === 'open' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'
                }`}>{r.status}</span>
                <span className="text-xs text-zinc-300 font-medium">{r.reason}</span>
              </div>
              <span className="text-[10px] text-zinc-600">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            <p className="text-[11px] text-zinc-500 mb-1">
              By: {r.reporter?.display_name || r.reporter?.username || 'Unknown'} | {r.target_type}: {r.target_id}
            </p>
            {r.details && <p className="text-xs text-zinc-400 mb-2">{r.details}</p>}
            {r.status === 'open' && (
              <Button onClick={() => resolveReport(r.report_id)} size="sm" variant="outline"
                className="border-white/10 text-xs h-7 hover:bg-green-500/10 hover:text-green-400" data-testid={`resolve-${r.report_id}`}>
                Mark Resolved
              </Button>
            )}
          </div>
        ))
      )}
    </div>
  );
};

